import { CancelPreviewClaimEntry } from '../api/claims';

/** The two headline figures from POST /ui/claims/<id>/cancel/preview. */
export interface BulkPreviewTotals {
  chargeableCount: number;
  estimatedAmount: number;
}

export type BulkFeeSummary =
  | { status: 'loading' }
  | { status: 'unavailable' }
  | { status: 'ready'; chargedCount: number; totalPounds: number; ringfencedCount: number };

/**
 * The fee the bulk "Confirm Cancellation Fee" modal shows for the claims the
 * staff member actually selected (Freshdesk #604). Never count × 216.
 *
 * Source: the side-effect-free preview HierarchicalClaimsTable already fetches
 * when the modal opens (cancelAllAssociated=true: every one of the client's
 * live claims). Charged claims are the selected ones the preview says will
 * be charged. The total is the backend's estimatedAmount when the selection
 * covers every chargeable claim; when staff excluded one, it is scaled by
 * claim count. The preview has no subset parameter, and the fee is linear per
 * claim (StripeService.calculate_total_amount: base × n × (1 + VAT)), so this
 * is exact at £180 + 20% VAT.
 *
 * Unknown is "unavailable", never a guess: a failed preview, a selected claim
 * the preview did not cover, or chargeable claims with no estimate.
 */
export function summariseBulkFee(input: {
  selectedClaimIds: string[];
  breakdownById: Record<string, CancelPreviewClaimEntry>;
  totals: BulkPreviewTotals | null;
  loading: boolean;
  failed: boolean;
}): BulkFeeSummary {
  if (input.loading) return { status: 'loading' };
  if (input.failed || !input.totals) return { status: 'unavailable' };
  const entries = input.selectedClaimIds.map((id) => input.breakdownById[id]);
  if (entries.length === 0 || entries.some((entry) => !entry)) return { status: 'unavailable' };

  const chargedCount = entries.filter((entry) => entry.willCharge).length;
  const ringfencedCount = entries.filter((entry) => entry.reasonCode === 'FREE_AGE_RINGFENCED').length;
  if (chargedCount === 0) return { status: 'ready', chargedCount: 0, totalPounds: 0, ringfencedCount };

  const { chargeableCount, estimatedAmount } = input.totals;
  if (chargeableCount <= 0 || !(estimatedAmount > 0)) return { status: 'unavailable' };
  const totalPounds = chargedCount === chargeableCount
    ? estimatedAmount
    : Math.round((estimatedAmount / chargeableCount) * chargedCount * 100) / 100;
  return { status: 'ready', chargedCount, totalPounds, ringfencedCount };
}

export function formatPounds(amount: number): string {
  return `£${amount.toFixed(2)}`;
}
